import React from 'react';
import { motion } from 'framer-motion';

import type { BanList, LadderRung } from '../../services/websocketService';

/**
 * THE RUNG, AND THE BAN, ON ONE LINE.
 *
 * This is the only place the player is told what they are standing on, and it has
 * to do two jobs at once without being a HUD:
 *
 *   1. NAME THE OPPONENT. "ROUND 4 · EXPERT" is not decoration. Atom's difficulty
 *      is REACHABILITY — which words it can draw — so a promoted Atom is a
 *      genuinely different opponent, and the player has earned the right to see
 *      it coming.
 *   2. SHOW THE BAN BEFORE THEY TYPE. A ban announced after you have written the
 *      word is not a rule, it is a trap. It rides in on the same event as the
 *      letter and it is on screen from the first frame of the race.
 *
 * There are NO NUMBERS on the ban chips, and there never will be — not "87% wrote
 * CAT", not "seen 3 times". A statistic must be true, and with fourteen users any
 * such number is a lie. A rule only has to be fair, and "banned: CAT" is fair on
 * day one because everybody faces it. The server enforces the shape (`getBanList`
 * returns bare words), and this component could not render a count if it tried.
 */

const DIFFICULTY_COLOR: Record<string, string> = {
  easy: 'var(--green, #1f8f4a)',
  medium: 'var(--green, #1f8f4a)',
  hard: 'var(--amber, #c08a2e)',
  expert: 'var(--coral, #b94a3b)',
  insane: 'var(--coral, #b94a3b)',
};

/** Who Atom is at this rung. Must match `PERSONA_BY_DIFFICULTY` on the server. */
const PERSONA: Record<string, string> = {
  easy: 'The Coach',
  medium: 'The Neighbour',
  hard: 'The Wordsmith',
  expert: 'The Rival',
  insane: 'The Rival',
};

export interface LadderBannerProps {
  rung: LadderRung;
  bans?: BanList | null;
  /** Compact drops the persona line — for the race, where every pixel is an input. */
  compact?: boolean;
}

export function LadderBanner({ rung, bans, compact = false }: LadderBannerProps) {
  const color = DIFFICULTY_COLOR[rung.difficulty] || 'var(--ink-2)';
  const banned = Object.entries(bans || {}).flatMap(([category, words]) =>
    (words || []).map(w => ({ category, display: w.display })),
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="wr-col gap-2"
      style={{
        padding: compact ? '8px 12px' : '12px 14px',
        borderRadius: 14,
        background: 'var(--surface)',
        border: `1.5px solid ${color}`,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div className="wr-row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.08em',
            color,
          }}
        >
          RUNG {rung.round} · {rung.difficulty.toUpperCase()}
        </span>
        {!compact && (
          <span className="wr-text-3" style={{ fontSize: 12 }}>
            Atom · {PERSONA[rung.difficulty] || 'The Neighbour'}
          </span>
        )}
        {/*
          THE ONLY NUMBER ON THIS BANNER, and it is the only one there is: the
          deepest you have ever been. It sits here rather than on a profile screen
          because it is the thing you are playing against.
        */}
        {!compact && (rung.bestDepth ?? 0) > 0 && (
          <span
            className="wr-text-3"
            style={{ fontSize: 11, marginLeft: 'auto', fontFamily: 'var(--mono)' }}
          >
            BEST {rung.bestDepth}
          </span>
        )}
      </div>

      {banned.length > 0 && (
        <div className="wr-row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: 'var(--coral, #b94a3b)',
            }}
          >
            BANNED
          </span>
          {banned.map(b => (
            <span
              key={`${b.category}-${b.display}`}
              title={b.category}
              style={{
                fontSize: 12,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 999,
                color: 'var(--coral, #b94a3b)',
                background: 'color-mix(in srgb, var(--coral, #b94a3b) 12%, transparent)',
                textDecoration: 'line-through',
                textDecorationThickness: '1.5px',
              }}
            >
              {b.display}
            </span>
          ))}
        </div>
      )}

      {!compact && rung.note && (
        <div className="wr-text-3" style={{ fontSize: 12, lineHeight: 1.35 }}>
          {rung.note}
        </div>
      )}
    </motion.div>
  );
}
